import { useNavigate } from 'react-router-dom';
import { Icon } from '@capsico/ui';
import type { CustomerOrderRow } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { useMyOrders } from '../hooks/useMyOrders';
import { money } from '../utils/money';
import { orderTime } from '../utils/format';
import { VirtualList } from '../components/VirtualList';
import { Button, EmptyState, ListSkeleton, Notice, PageHeader } from '../components/ui';

const ROW_HEIGHT = 108;

/** Your orders (C20): one card per order — restaurant, items summary, total, stage chip, time. */
export function OrdersPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const list = useMyOrders(true);

  const tone = (row: CustomerOrderRow) =>
    row.stage === 'delivered'
      ? 'bg-positive-bg text-positive'
      : row.stage === 'cancelled'
        ? 'bg-negative-bg text-negative'
        : 'bg-brand-soft text-ink';

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('orders.title')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
      />
      <div className="px-4 pt-4">
        {list.loading ? (
          <ListSkeleton rows={4} />
        ) : list.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={list.retry}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : list.rows.length === 0 ? (
          <EmptyState
            icon="receipt"
            title={t('orders.empty')}
            action={<Button onClick={() => navigate('/')}>{t('cart.browse')}</Button>}
          >
            {t('orders.emptyHint')}
          </EmptyState>
        ) : (
          <VirtualList<CustomerOrderRow>
            items={list.rows}
            rowHeight={ROW_HEIGHT}
            getKey={(row) => row.id}
            renderRow={(row) => (
              <div className="h-full pb-3">
                <button
                  type="button"
                  onClick={() => navigate(`/orders/${row.id}`)}
                  className="flex h-full w-full flex-col justify-between rounded-sheet bg-surface p-4 text-left shadow-[inset_0_0_0_1px_var(--color-hair)] active:bg-tile"
                >
                  <span className="flex w-full items-start gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold text-ink">
                        {row.outlet.name}
                      </span>
                      <span className="block truncate text-caption text-ink-2">
                        {row.itemsSummary}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-micro font-semibold ${tone(row)}`}
                    >
                      {t(`stage.${row.stage}`)}
                    </span>
                  </span>
                  <span className="flex w-full items-center justify-between text-caption text-ink-3">
                    <span>
                      {t('orders.number', { number: row.number })} · {orderTime(row.placedAt)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-ink tabular">
                      {money(row.country, row.totalMinor)}
                      <Icon name="chevron-right" size={14} />
                    </span>
                  </span>
                </button>
              </div>
            )}
          />
        )}
      </div>
    </div>
  );
}
